import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Document, Page, pdfjs } from 'react-pdf'
import { KibButtonNew } from '@chewy/kib-controls-react'
import {
  KibDownloadIcon,
  KibEmailFillIcon,
  KibLaunchIcon,
  KibPrintIcon,
} from '@chewy/kib-icons-react'
import { KibSpinningLoader } from '@chewy/kib-overlays-react'

import { PDF } from '~/constants/extensions'
import { downloadFile, getFileName } from '~/utils/file'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import '@chewy/kib-overlays-styles/dist/styles/kib-spinning-loader.css'
import '@chewy/kib-interstitials-styles/dist/styles/kib-spinner.css'
import styles from './FileViewer.module.scss'

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`

export interface FileViewerProps {
  disableDownload?: boolean
  disableEmail?: boolean
  disablePrint?: boolean
  disableShare?: boolean
  fileUrl: string
  /**
   * When true, the container will adjust its height to fit the entire document without scrolling
   */
  fitHeightToDocument?: boolean
  onDownload?: () => void
  onEmail?: () => void
  onPreview?: () => void
  onPrint?: () => void
}

const FileViewer = ({
  disableDownload,
  disableEmail,
  disablePrint,
  disableShare,
  fileUrl,
  fitHeightToDocument = false,
  onDownload,
  onEmail,
  onPreview,
  onPrint,
}: FileViewerProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [pageCount, setPageCount] = useState(1)
  const [containerWidth, setContainerWidth] = useState(0)
  const [documentHeight, setDocumentHeight] = useState<number | null>(null)
  const [renderedPages, setRenderedPages] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const { t } = useTranslation()

  // Update container width when component mounts and when window resizes
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth)
      }
    }

    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [fitHeightToDocument])

  const handleEmail = useCallback(() => {
    if (onEmail) {
      onEmail()
    } else {
      // Fallback to mailto link
      window.open(`mailto:?body=${encodeURIComponent(fileUrl)}`, '_blank')
    }
  }, [onEmail, fileUrl])

  const handlePrint = useCallback(() => {
    if (onPrint) {
      onPrint()
    } else {
      window.open(fileUrl, '_blank')
    }
  }, [onPrint, fileUrl])

  const handleDownload = useCallback(() => {
    if (onDownload) {
      onDownload()
    } else {
      downloadFile({
        url: fileUrl,
        name: getFileName(fileUrl),
        extension: PDF,
      })
    }
  }, [onDownload, fileUrl])

  const handlePreview = useCallback(() => {
    if (onPreview) {
      onPreview()
    } else {
      window.open(fileUrl, '_blank')
    }
  }, [onPreview, fileUrl])

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setPageCount(numPages)
    setRenderedPages(0) // Reset rendered pages count when document loads
  }

  // Handle page render success to calculate total height
  const onPageRenderSuccess = useCallback(() => {
    setIsLoading(false)
    if (fitHeightToDocument) {
      setRenderedPages((prev) => prev + 1)
    }
  }, [fitHeightToDocument])

  // Calculate document height when all pages are rendered
  useEffect(() => {
    if (
      fitHeightToDocument &&
      pageCount > 0 &&
      !isLoading &&
      containerRef.current
    ) {
      // Use a small delay to ensure DOM is fully updated
      const timeoutId = setTimeout(() => {
        const pdfElement = containerRef.current?.querySelector(
          '.react-pdf__Document',
        )
        if (pdfElement) {
          setDocumentHeight(pdfElement.getBoundingClientRect().height + 32)
        }
      }, 50)

      return () => clearTimeout(timeoutId)
    }
    return undefined
  }, [fitHeightToDocument, renderedPages, pageCount])

  const containerStyle = useMemo(() => {
    if (fitHeightToDocument && documentHeight) {
      return { height: `${documentHeight}px` }
    }
    return undefined
  }, [fitHeightToDocument, documentHeight])

  return (
    <div
      className={styles.container}
      data-testid="file-viewer"
      ref={containerRef}
      style={containerStyle}
    >
      <div className={styles.actionButtons}>
        {!disableShare && (
          <KibButtonNew
            iconOnly
            className={styles.actionButton}
            data-testid="preview-button"
            emphasis="primary"
            icon={<KibLaunchIcon />}
            theme="utility"
            onClick={handlePreview}
          />
        )}
        {!disableEmail && (
          <KibButtonNew
            iconOnly
            className={styles.actionButton}
            data-testid="email-button"
            emphasis="primary"
            icon={<KibEmailFillIcon />}
            theme="utility"
            onClick={handleEmail}
          />
        )}
        {!disablePrint && (
          <KibButtonNew
            iconOnly
            className={styles.actionButton}
            data-testid="print-button"
            emphasis="primary"
            icon={<KibPrintIcon />}
            theme="utility"
            onClick={handlePrint}
          />
        )}
        {!disableDownload && (
          <KibButtonNew
            iconOnly
            className={styles.actionButton}
            data-testid="download-button"
            emphasis="primary"
            icon={<KibDownloadIcon />}
            theme="utility"
            onClick={handleDownload}
          />
        )}
      </div>
      <div className={styles.fileContainer}>
        {isLoading && <KibSpinningLoader embedded />}
        <Document
          error={t('Errors:PDF_LOAD_ERROR')}
          file={{ url: fileUrl, withCredentials: true }}
          loading={<div className={styles.loading} />}
          onLoadSuccess={onDocumentLoadSuccess}
        >
          {Array.from({ length: pageCount }, (_, index) => (
            <Page
              key={index}
              pageNumber={index + 1}
              width={containerWidth}
              onRenderSuccess={onPageRenderSuccess}
            />
          ))}
        </Document>
      </div>
    </div>
  )
}

export default FileViewer
